import {Component, inject} from '@angular/core';
import {TitlePagesComponent} from '@/app/shared/components/title-pages-component/title-pages-component';
import {AppConstants} from '@/app/constants/AppConstants';
import {FormBuilder, FormControl, FormsModule, ReactiveFormsModule, Validators,} from '@angular/forms';
import {SnackbarService} from '@/app/shared/services/snackbar.service';
import {
  TableWithInputsComponent
} from '@/app/shared/components/table-with-inputs-component/table-with-inputs-component';
import {ConfirmDeleteDialog} from '@/app/shared/components/confirm-delete-dialog/confirm-delete-dialog';
import {noSoloEspacios} from '../../clientes/pages/clientes-form-component/validations';
import {DepartamentService} from '../services/departament.service';
import {Departament, DepartamentPost,} from '../services/departament.interface';
import {NgClass} from '@angular/common';
import {ActivatedRoute, Router} from '@angular/router';
import {ClientStore} from '@/app/core/states/clientes-state/client-state.store';
import {Client} from '../../cliente/models/cliente.interface';
import {BulkParseService} from '@/app/shared/services/bulk-upload-modal/bulk-parse.service';
import {BulkUploadModalComponent} from '@/app/shared/components/bulk-upload-modal/bulk-upload-modal.component';
import {ModalPasosCampana} from '@/app/shared/components/modal-pasos-campana/modal-pasos-campana';

@Component({
  selector: 'app-departamento-component',
  standalone: true,
  imports: [
    TitlePagesComponent,
    TableWithInputsComponent,
    FormsModule,
    ReactiveFormsModule,
    NgClass,
    ConfirmDeleteDialog,
    BulkUploadModalComponent,
    ModalPasosCampana,
  ],
  templateUrl: './departamento-component.html',
  styleUrl: './departamento-component.scss',
})
export class DepartamentoComponent {
  pageData = {
    title: AppConstants.DEPARTAMENT_TITLE,
    subTitle: AppConstants.DEPARTAMENT_SUBTITLE,
  };
  // definicion de columnas para el componente tabla;
  columnDefinitions = [
    { key: 'name', displayName: 'Departamento' },
    { key: 'acciones', displayName: 'Acciones' },
  ];
  protected readonly AppConstants = AppConstants;
  protected departaments: Departament[] = [];
  // para el formulario
  protected showForm: boolean = false;
  protected submitted: boolean = false;
  // para mostrar u ocultar el boton de nuevo
  protected showBtn: boolean = true;
  // para el modar de confirmación de eliminacion
  protected showModal: boolean = false;
  protected message!: string;
  protected departamentToDelete!: Departament;
  protected showBulkUploadModal = false;
  protected iconActions: {
    label: string;
    icon: string;
    action: (item: any) => void;
  }[] = [
    {
      label: 'Ver ocupaciones',
      icon: 'addNew.svg',
      action: (item) => {
        this.navigateToOcupaciones(item.id);
      },
    },
  ];
  protected showModalPasosCampaign!: boolean;

  private formBuilder: FormBuilder = inject(FormBuilder);
  // Formulario para registrar el departamento
  formDepartament = this.formBuilder.group({
    name: new FormControl('', [Validators.required, noSoloEspacios()]),
    description: new FormControl(''),
  });
  private departamentService: DepartamentService = inject(DepartamentService);
  private store = inject(ClientStore);
  private snackbarService = inject(SnackbarService);

  //Formulario para registrar la ocupación;
  private router: Router = inject(Router);
  private bulkParse = inject(BulkParseService);
  private route = inject(ActivatedRoute);

  private client!: Client | null;

  get name() {
    return this.formDepartament.get('name');
  }

  get description() {
    return this.formDepartament.get('description');
  }

  ngOnInit(): void {
    this.showModalPasosCampaign = this.route.snapshot.queryParams['showModal'] === 'true';

    this.client = this.store.client();
    if (this.client) {
      // Los departamentos ya deberían estar en cache desde el flujo anterior
      // Solo los cargamos si no están disponibles
      if (this.store.departaments().length === 0) {
        this.departamentService
          .getDepartaments(this.client.publicId)
          .subscribe((data: Departament[]) => {
            this.departaments = data;
            this.store.setDepartaments(this.departaments);
          });
      } else {
        // Usar datos del cache
        this.departaments = this.store.departaments();
      }
    }
  }

  // ===== CARGA MASIVA =====
  async onBulkConfirm(file: File) {
    try {
      // Lee y valida la plantilla: título + encabezados "Nombre" / "Descripción"
      const { validNames, invalid, duplicates } =
        await this.bulkParse.parseCreateFile(file);

      // 1) Priorizar error de FORMATO / ENCABEZADOS (servicio marca row:1)
      const headerIssue = invalid.find((e) => e.row === 1);
      if (headerIssue) {
        this.snackbarService.showError(headerIssue.reason);
        return;
      }

      // 2) Si hay otras filas inválidas (datos), no enviamos
      if (invalid.length > 0) {
        this.snackbarService.showError(
          `Archivo con errores. Filas inválidas: ${invalid.length}. Duplicadas internas: ${duplicates.length}.`
        );
        return;
      }

      // 3) Sin inválidas pero sin filas válidas
      if (validNames.length === 0) {
        this.snackbarService.showError('No se encontraron filas válidas.');
        return;
      }

      // 4) Construir payload [{ name }] (de momento solo enviamos nombre)
      const payload = validNames.map((nombre) => ({ name: nombre }));

      // 5) Quitar los que ya existen en la tabla local para evitar 409
      const existing = new Set(
        this.departaments.map((d) => d.name.trim().toLowerCase())
      );
      const payloadFiltered = payload.filter(
        (p) => !existing.has(p.name.trim().toLowerCase())
      );
      const alreadyExisting = payload.length - payloadFiltered.length;

      if (payloadFiltered.length === 0) {
        this.snackbarService.showError(
          'Todos los registros del archivo ya existen. No se enviaron datos.'
        );
        return;
      }

      // 6) POST batch a departamentos

      if (!this.client) {
        return;
      }
      this.departamentService
        .createDepartmentsBatch(this.client.publicId, payloadFiltered)
        .subscribe({
          next: (created) => {
            this.departaments = [...this.departaments, ...created];
            this.snackbarService.showSuccess(
              `Creados: ${created.length}. Duplicadas en el archivo: ${duplicates.length}` +
                (alreadyExisting > 0 ? `. Ya existían: ${alreadyExisting}` : '')
            );
            this.showBulkUploadModal = false;
            // Fijaba validateOccupation por error; aquí corresponde Departament
            localStorage.removeItem('validateDepartament');
          },
          error: (err) => {
            if (err?.status === 409) {
              this.snackbarService.showError(
                'Algunos nombres ya existen y no se pudieron crear. Revisa tu archivo y la lista actual.'
              );
            } else {
              this.snackbarService.showError(
                `Error al crear departamentos en lote: ${
                  err?.message ?? 'desconocido'
                }`
              );
            }
          },
        });
    } catch (e: any) {
      this.snackbarService.showError(
        `No se pudo leer el archivo: ${e?.message ?? e}`
      );
    }
  }

  onBulkClose() {
    this.showBulkUploadModal = false;
  }

  // Opción 1 del menú → mostrar form
  handleAddSingle(): void {
    this.showForm = true;
    this.showBtn = false;
  }

  // Opción 2 del menú → carga masiva
  handleBulkUpload(): void {
    this.showBulkUploadModal = true;
  }

  // ===== CRUD individual =====
  handleCancelAdd(): void {
    this.showForm = false;
    this.showBtn = true;
  }

  //realiza el guardado del departamento
  handleSubmit() {
    this.submitted = true;
    if (this.formDepartament.invalid) {
      return;
    }

    const departament: DepartamentPost = {
      name: this.formDepartament.value.name ?? '',
    };

    if (!this.client) {
      return;
    }

    this.departamentService
      .createDepartament(this.client.publicId, departament)
      .subscribe({
        next: (createdDepartament: Departament) => {
          this.departaments.push(createdDepartament);
          this.snackbarService.showSuccess('Departamento creado con éxito');
          this.formDepartament.reset();
          this.showForm = false;
          this.showBtn = true;
          this.store.setDepartaments(this.departaments);
        },
        error: (err) => {
          this.snackbarService.showError(
            'Error al crear el departamento' + err.message
          );
        },
      });
  }

  //editar registro de departamento
  handleSaveEdit(item: any) {
    if (!this.client) {
      return;
    }
    this.departamentService
      .updateDepartament(item.id, item, this.client.publicId)
      .subscribe({
        next: (updatedItem) => {
          const index = this.departaments.findIndex((o) => o.id === item.id);
          if (index !== -1) {
            this.departaments[index] = { ...item };
          }
          this.snackbarService.showSuccess('Departamento editado con éxito');
        },
        error: (err) => {
          this.snackbarService.showError(
            'Error al editar el departamento' + err.message
          );
        },
      });
  }

  //muestra el modal de confirmación de eliminación
  showConfirmDeleteDialog = (departament: Departament) => {
    //mensaje para la confirmación de eliminacion
    this.message = 'Se eliminará el departamento: ' + departament.name;
    this.departamentToDelete = departament;
    this.showModal = true;
  };

  //cierra el modal de confirmación de eliminación
  handleCancelDeleteDialog() {
    this.showModal = false;
  }

  //elimina el registro de departamento
  handleDelete() {
    if (!this.client) {
      return;
    }

    this.departamentService
      .deleteDepartament(this.departamentToDelete.id, this.client.publicId)
      .subscribe({
        next: () => {
          this.showModal = false;
          this.departaments = this.departaments.filter(
            (o) => o.id !== this.departamentToDelete.id
          );
          this.store.setDepartaments(this.departaments);
          this.snackbarService.showSuccess('Departamento eliminado con éxito');
        },
        error: (err) => {
          this.snackbarService.showError(
            'Error al eliminar el departamento: ' + err.message
          );
        },
      });
  }

  // Descarga de formato (si lo manejas desde el padre con (downloadFormat))
  onDescargarFormato(resource: string) {
    this.snackbarService.showInfo(`Descargando formato de ${resource}…`);
  }

  navigateToOcupaciones(departmentId: number) {
    this.router.navigate(['/clientes/ocupacion'], {
      queryParams: { departmentId },
    });
  }

  closeModalPasosCampaign(): void {
    this.showModalPasosCampaign = false;
  }
}
